// exportUtils.js — Excel/PDF export utilities for portfolio analytics
import * as XLSX from 'xlsx';

// ─── Column Definitions ────────────────────────────────────────────────────────

export const HOLDINGS_EXPORT_COLUMNS = [
  { header: 'ID', key: 'id' },
  { header: 'Entity', key: 'entity' },
  { header: 'Country', key: 'country' },
  { header: 'Instrument', key: 'instrument' },
  { header: 'ISIN', key: 'isin' },
  { header: 'Currency', key: 'currency' },
  { header: 'Face Value', key: 'faceValue', format: 'number' },
  { header: 'Purchase Price', key: 'purchasePrice', format: 'number' },
  { header: 'Purchase Date', key: 'purchaseDate' },
  { header: 'Maturity Date', key: 'maturityDate' },
  { header: 'Coupon Rate', key: 'couponRate', format: 'percent' },
  { header: 'YTM', key: 'yieldToMaturity', format: 'percent' },
  { header: 'Status', key: 'status' },
  { header: 'Foreign Exchange Rate (Purchase)', key: 'fxRateAtPurchase', format: 'number' },
  { header: 'Foreign Exchange Rate (Current)', key: 'currentFxRate', format: 'number' },
  { header: 'Custodian Bank', key: 'custodianBank' },
];

export const CASHFLOW_EXPORT_COLUMNS = [
  { header: 'Date', key: 'date' },
  { header: 'Type', key: 'type' },
  { header: 'Position ID', key: 'positionId' },
  { header: 'Instrument', key: 'instrument' },
  { header: 'Country', key: 'country' },
  { header: 'Currency', key: 'currency' },
  { header: 'Amount (Local)', key: 'amountLocal', format: 'number' },
  { header: 'Amount (USD)', key: 'amountUsd', format: 'number' },
  { header: 'Auto-Roll', key: 'autoRoll', format: 'boolean' },
];

export const PERFORMANCE_EXPORT_COLUMNS = [
  { header: 'Month', key: 'month' },
  { header: 'TWR', key: 'twr', format: 'percent' },
  { header: 'MWR', key: 'mwr', format: 'percent' },
  { header: 'Cumulative TWR', key: 'cumTwr', format: 'percent' },
  { header: 'Cumulative MWR', key: 'cumMwr', format: 'percent' },
];

export const RISK_LIMITS_EXPORT_COLUMNS = [
  { header: 'Limit Name', key: 'name' },
  { header: 'Category', key: 'category' },
  { header: 'Limit', key: 'limit' },
  { header: 'Current', key: 'current' },
  { header: 'Unit', key: 'unit' },
];

export const STRESS_EXPORT_COLUMNS = [
  { header: 'Scenario', key: 'name' },
  { header: 'Description', key: 'description' },
  { header: 'Portfolio Value Impact', key: 'portfolioValueImpact', format: 'percent' },
  { header: 'Yield Change Impact', key: 'yieldChangeImpact', format: 'percent' },
  { header: 'Foreign Exchange Impact', key: 'fxImpact', format: 'percent' },
  { header: 'Net Impact', key: 'netImpact', format: 'percent' },
];

// ─── Format Helpers ─────────────────────────────────────────────────────────────

function formatCell(value, format) {
  if (value === null || value === undefined) return '';
  switch (format) {
    case 'percent':
      return typeof value === 'number' ? (value * 100).toFixed(2) + '%' : value;
    case 'number':
      return typeof value === 'number' ? value : value;
    case 'boolean':
      return value ? 'Yes' : 'No';
    default:
      return value;
  }
}

// ─── Export Functions ───────────────────────────────────────────────────────────

/**
 * Export data to an Excel file (.xlsx).
 * @param {Object[]} data - Array of data objects
 * @param {Object[]} columns - Column definitions with { header, key, format? }
 * @param {string} sheetName - Name for the worksheet
 * @param {string} fileName - Output file name (without extension)
 */
export function exportToExcel(data, columns, sheetName, fileName) {
  const headerRow = columns.map(c => c.header);
  const dataRows = data.map(row =>
    columns.map(col => formatCell(row[col.key], col.format))
  );

  const aoa = [headerRow, ...dataRows];
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Set column widths based on header length
  if (ws) {
    ws['!cols'] = columns.map(col => ({
      wch: Math.max(col.header.length + 2, 14)
    }));
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${fileName}.xlsx`);
}

/**
 * Export multiple sheets to a single Excel file.
 * @param {Object[]} sheets - Array of { data, columns, sheetName }
 * @param {string} fileName - Output file name (without extension)
 */
export function exportMultiSheetExcel(sheets, fileName) {
  const wb = XLSX.utils.book_new();

  sheets.forEach(({ data, columns, sheetName }) => {
    const headerRow = columns.map(c => c.header);
    const dataRows = data.map(row =>
      columns.map(col => formatCell(row[col.key], col.format))
    );

    const aoa = [headerRow, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    if (ws) {
      ws['!cols'] = columns.map(col => ({
        wch: Math.max(col.header.length + 2, 14)
      }));
    }

    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });

  XLSX.writeFile(wb, `${fileName}.xlsx`);
}

/**
 * Trigger browser print dialog with portfolio-optimized styling.
 * Adds `print-portfolio` class to body, calls window.print(), then removes it.
 */
export function exportToPdf() {
  document.body.classList.add('print-portfolio');
  window.print();
  document.body.classList.remove('print-portfolio');
}
